import React, { useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { getDecryptedToken } from "../../../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../../../utils/currency/currencyUtils";
import baseUrl from "../../../../../api/baseUrl";

type Props = {
  open: boolean;
  onClose: () => void;
  posStatus: any | null;
  posHistory?: any[]; // kept for compatibility but not shown
};

const CashManagementPrint: React.FC<Props> = ({ open, onClose, posStatus }) => {
  const shopName = (getDecryptedToken("martName") || "").toString();
  const cashierName = (getDecryptedToken("name") || "").toString();
  const currency = getCurrencySymbol();
  // Replace tableData with summaryData (daily totals) to include in bill
  const [summaryData, setSummaryData] = React.useState<any | null>(null);

  const formatAmount = (v: any) => {
    const n = Number(v || 0);
    return (Math.round((n + Number.EPSILON) * 100) / 100).toFixed(2);
  };

  const isMmk =
    typeof shopName === "string" &&
    shopName.trim().toLowerCase() === "mmk chiba";

  // current date/time to show on receipt
  const now = new Date().toLocaleString();

  // fetch daily totals summary used on bill
  const fetchSummary = useCallback(() => {
    // If sessionId is provided in posStatus, use it; otherwise use date
    const params: any = {};

    if (posStatus?.sessionId) {
      params.sessionId = posStatus.sessionId;
    } else {
      // prefer posStatus.date if provided, else use today's local date (YYYY-MM-DD)
      params.date = (posStatus && posStatus.date) || now.slice(0, 10);
    }

    baseUrl
      .get("branch/sale/daily-report-summery", { params })
      .then((res) => {
        const payload = res.data?.data ?? null;
        const summary = payload?.summary ?? payload ?? null;
        setSummaryData(summary);
      })
      .catch(() => {});
  }, [posStatus]);

  // Re-fetch when posStatus changes (so date from posStatus is picked up)
  React.useEffect(() => {
    fetchSummary();
  }, [fetchSummary, posStatus]);

  const handlePrint = () => {
    // Build a minimal HTML document containing only the receipt area and styles,
    // then open it in a new window and print that window.
    const receiptHtml = `
      <!doctype html>
      <html>
      <head>
      <meta charset="utf-8"/>
      <title>Cash Management Receipt</title>
      <style>
        @page {
          size: 80mm auto;
          margin: 0;
          padding: 0;
        }
        * {
          margin: 0;
          padding: 0;
        }
        html, body {
          margin: 0;
          padding: 0;
          background: white;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }
        .receipt {
          background-color: white;
          width: 350px;
          margin: 0;
          padding: 5px 8px;
          box-sizing: border-box;
          font-family: "Stick No Bills", "Arial Narrow", Arial, sans-serif;
          text-transform: uppercase;
        }
        .receipt.mmk-chiba { font-family: "Arial Narrow", Arial, sans-serif !important; font-weight: 700 !important; text-transform: uppercase !important; }
        .receipt > div { padding: 5px 0; }
        .receipt hr { border: 1px dotted; }
        .receipt .headers { width:100%; display:flex; flex-direction:column; align-items:center; }
        .receipt .headers .title { font-size: 24px; font-weight:700; }
        .receipt .row { display:flex; justify-content:space-between; gap:1px; margin:3px 0; white-space: nowrap; overflow-wrap: break-word; font-size: 14px; padding: 0 1px; }
        .receipt .section-title { text-align:center; font-size:18px; font-weight:700; margin-top:6px; margin-bottom:4px; }
        .receipt .thank { display:flex; justify-content:center; align-items:center; height:40px; font-size:14px; margin-top:8px; }
        @media print {
        .receipt { width: 80mm !important; min-width: 80mm; max-width: 80mm; background: white !important; box-shadow:none !important; margin:0 !important; padding:5px 8px !important; box-sizing: border-box !important; }
        .receipt > div { padding: 1px 0 !important; }
        .receipt .headers .title { font-size: 24px !important; line-height: 1.1 !important; }
        .receipt .thank { font-size: 13px !important; height: 28px !important; }
        .receipt .row { font-size: 14px !important; }
        body { margin: 0 !important; background: white !important; display: flex !important; justify-content: center !important; }
        }
      </style>
      </head>
      <body>
      <div class="receipt ${isMmk ? "mmk-chiba" : ""}">
        <div class="headers">
        <div class="title">${(shopName || "SHOP")
          .toString()
          .replace(/</g, "&lt;")}</div>
        <div class="time" style="font-size:13px; margin-top:4px;">${now}</div>
        <div class="cashier" style="font-size:13px; margin-top:2px;">Cashier: ${(
          cashierName || ""
        ).replace(/</g, "&lt;")}</div>
        </div>
        <hr/>
        <div style="padding:6px 10px">
        <div class="section-title">CASH MANAGEMENT</div>
        <div class="row"><div>Cash In</div><div>${currency} ${formatAmount(
          posStatus?.cashIn,
        )}</div></div>  
    <div class="row"><div>Cash Sales</div><div>${currency} ${formatAmount(
      summaryData?.totalCash,
    )}</div></div>
        <div class="row"><div>Cash Out</div><div>${currency} ${formatAmount(
          posStatus?.cashOut,
        )}</div></div>
        <div class="row"><div>Total Sale Return</div><div>${currency} ${formatAmount(
          summaryData?.totalRefunds,
        )}</div></div>
        <div class="row"><div>Current Balance</div><div>${currency} ${formatAmount(
          Number(posStatus?.currentBalance || 0) -
            Number(summaryData?.totalRefunds || 0),
        )}</div></div>
        <!-- Summary rows from daily-report-summery -->
${
  summaryData
    ? `
  <hr/>
  <div class="section-title">SUMMARY</div>
  <div class="row"><div>Total Sales</div><div>${currency} ${formatAmount(
    summaryData.totalSales,
  )}</div></div>
  <div class="row"><div>Cash Sales</div><div>${currency} ${formatAmount(
    summaryData.totalCash,
  )}</div></div>
  <div class="row"><div>Card Sales</div><div>${currency} ${formatAmount(
    summaryData.totalCard,
  )}</div></div>
  <div class="row"><div>Bank Sales</div><div>${currency} ${formatAmount(
    summaryData.totalBank,
  )}</div></div>
  <div class="row"><div>Credit Sales</div><div>${currency} ${formatAmount(
    summaryData.totalCreditSales,
  )}</div></div>
      <div class="row"><div>Total Sale Return</div><div>${currency} ${formatAmount(
        summaryData.totalRefunds,
      )}</div></div>
          <div class="row"><div>Total Discount</div><div>${currency} ${formatAmount(
            summaryData.totalDiscount,
          )}</div></div>
  <div class="row"><div>Net Sales</div><div>${currency} ${formatAmount(
    (summaryData.totalSales || 0) -
      (summaryData.totalRefunds || 0) -
      (summaryData.totalDiscount || 0),
  )}</div></div>
`
    : ""
}
         <hr/>
         <div class="thank">Thank You</div>
         </div>
       </div>
       <script>
         window.onload = function() {
         setTimeout(function(){ window.print(); window.close(); }, 100);
         };
       </script>
       </body>
       </html>
     `;

    const printWindow = window.open("", "_blank", "width=400,height=800");
    if (!printWindow) {
      alert("Unable to open print window. Please allow popups for this site.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
        Print Cash Management
      </DialogTitle>
      <DialogContent dividers>
        {/* Inline CSS follows the structure you provided (flattened for CSS) */}
        <style>{`
          .receipt {
            background-color: white;
            width: 350px;
            box-sizing: border-box;
          }
          .receipt > * {
            font-family: "Stick No Bills", "Arial Narrow", Arial, sans-serif;
            text-transform: uppercase;
          }
          .receipt.mmk-chiba > * {
            font-family: "Arial Narrow", Arial, sans-serif !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
          }
          .receipt > div { padding: 5px 0; }
          .receipt hr { border: 1px dotted; }
          .receipt .headers { width:100%; display:flex; flex-direction:column; align-items:center; }
          .receipt .headers .title { font-size: 24px; font-weight:700; }
          .receipt .row { display:flex; justify-content:space-between; gap:1px; margin:3px 0; white-space: nowrap; overflow-wrap: break-word; font-size: 14px; padding: 0 1px; }
          .receipt .section-title { text-align:center; font-size:18px; font-weight:700; margin-top:6px; margin-bottom:4px; }
          .receipt .thank { display:flex; justify-content:center; align-items:center; height:40px; font-size:14px; margin-top:8px; }
          .receipt .time { font-size:13px; margin-top:4px; }
          /* print-specific */
          @media print {
            .receipt { width: 80mm !important; min-width: 80mm; max-width: 80mm; background: white !important; box-shadow:none !important; margin:0 !important; padding:5px 8px !important; box-sizing: border-box !important; }
            .receipt > div { padding: 1px 0 !important; }
            .receipt .headers .title { font-size: 24px !important; line-height: 1.1 !important; }
            .thank { font-size: 13px !important; height: 30px !important; }
            .receipt .row { font-size: 14px !important; }
            body { margin: 0 !important; background: white !important; display: flex !important; justify-content: center !important; }
          }
        `}</style>

        <Box className={`receipt${isMmk ? " mmk-chiba" : ""}`}>
          <div className="headers">
            <div className="title">{shopName || "SHOP"}</div>
            <div className="time" style={{ fontSize: 10, marginTop: 4 }}>
              {now}
            </div>
            {/* optional address/phone can be added here if needed */}
          </div>

          <hr />

          <div style={{ padding: "6px 10px" }}>
            <div className="section-title">CASH MANAGEMENT</div>

            <div className="row">
              <div>Cash In</div>
              <div>
                {currency} {formatAmount(posStatus?.cashIn)}
              </div>
            </div>

            <div className="row">
              <div>Cash Sales</div>
              <div>
                {currency} {formatAmount(summaryData?.totalCash)}
              </div>
            </div>

            <div className="row">
              <div>Cash Out</div>
              <div>
                {currency} {formatAmount(posStatus?.cashOut)}
              </div>
            </div>

            <div className="row">
              <div>Total Sale Return</div>
              <div>
                {currency} {formatAmount(summaryData?.totalRefunds)}
              </div>
            </div>

            <div className="row">
              <div>Current Drawer Balance</div>
              <div>
                {currency}{" "}
                {formatAmount(
                  Number(posStatus?.currentBalance || 0) -
                    Number(summaryData?.totalRefunds || 0),
                )}
              </div>
            </div>

            {/* Summary section displayed on-screen */}
            {summaryData && (
              <>
                <hr />
                <div className="section-title">SUMMARY</div>

                <div className="row">
                  <div>Total Sales</div>
                  <div>
                    {currency} {formatAmount(summaryData.totalSales)}
                  </div>
                </div>
                <div className="row">
                  <div>Cash Sales</div>
                  <div>
                    {currency} {formatAmount(summaryData.totalCash)}
                  </div>
                </div>
                <div className="row">
                  <div>Card Sales</div>
                  <div>
                    {currency} {formatAmount(summaryData.totalCard)}
                  </div>
                </div>
                <div className="row">
                  <div>Bank Sales</div>
                  <div>
                    {currency} {formatAmount(summaryData.totalBank)}
                  </div>
                </div>
                <div className="row">
                  <div>Credit Sales</div>
                  <div>
                    {currency} {formatAmount(summaryData.totalCreditSales)}
                  </div>
                </div>
                <div className="row">
                  <div>Total Discount</div>
                  <div>
                    {currency} {formatAmount(summaryData.totalDiscount)}
                  </div>
                </div>
                <div className="row">
                  <div>Total Sale Return</div>
                  <div>
                    {currency} {formatAmount(summaryData.totalRefunds)}
                  </div>
                </div>
                <div className="row">
                  <div>Net Sales</div>
                  <div>
                    {currency}{" "}
                    {formatAmount(
                      (summaryData.totalSales || 0) -
                        (summaryData.totalRefunds || 0) -
                        (summaryData.totalDiscount || 0),
                    )}
                  </div>
                </div>
              </>
            )}

            <hr />

            <div className="thank">Thank You</div>
          </div>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handlePrint} disabled={!posStatus}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashManagementPrint;
